/**
 * User-vs-typical spending benchmark card. Shows actual %-of-spend against
 * the South Asian wedding typical range, with a labeled status per category.
 * Degrades gracefully to "reference ranges" when the user has no expenses yet.
 */
import React from 'react';
import { Card } from '../ui/card';
import { GraphUp, InfoCircle } from 'iconoir-react';

function InsightRow({ ins }) {
  const typical = ins.benchmark_typical;
  const low = ins.benchmark_low ?? typical;
  const high = ins.benchmark_high ?? typical;
  const barMax = Math.max(ins.user_pct, high, 40);
  const statusColor =
    ins.status === 'above' ? 'text-terracotta' :
    ins.status === 'below' ? 'text-ochre' : 'text-moss';
  const statusLabel =
    ins.status === 'above' ? 'Above typical' :
    ins.status === 'below' ? 'Below typical' : 'On track';
  const fillBg =
    ins.status === 'above' ? 'bg-terracotta' :
    ins.status === 'below' ? 'bg-ochre' : 'bg-moss';
  return (
    <div className="group" data-testid={`benchmark-row-${ins.category}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-charcoal">{ins.label}</span>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-charcoal">{ins.user_pct}%</span>
          <span className="text-muted">vs</span>
          <span className="font-mono text-muted">{typical}%</span>
          <span className={`${statusColor} font-medium ml-2`}>{statusLabel}</span>
        </div>
      </div>
      <div className="relative h-2 bg-page rounded-sm overflow-hidden">
        <div
          className="absolute top-0 h-full bg-border/70"
          style={{ left: `${(low / barMax) * 100}%`, width: `${((high - low) / barMax) * 100}%` }}
          title={`Typical range: ${low}% – ${high}%`}
        />
        <div
          className="absolute top-0 h-full w-0.5 bg-charcoal/50"
          style={{ left: `calc(${(typical / barMax) * 100}% - 1px)` }}
          title={`Typical: ${typical}%`}
        />
        <div
          className={`absolute top-0 h-full rounded-sm ${fillBg} opacity-80`}
          style={{ width: `${(ins.user_pct / barMax) * 100}%` }}
        />
      </div>
    </div>
  );
}

function ReferenceRow({ cat, b }) {
  const barMax = 40;
  return (
    <div data-testid={`benchmark-ref-${cat}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-charcoal">{b.label}</span>
        <span className="text-xs font-mono text-muted">{b.low}–{b.high}% (typical {b.typical}%)</span>
      </div>
      <div className="relative h-2 bg-page rounded-sm overflow-hidden">
        <div
          className="absolute top-0 h-full bg-terracotta/30 rounded-sm"
          style={{ left: `${(b.low / barMax) * 100}%`, width: `${((b.high - b.low) / barMax) * 100}%` }}
        />
        <div
          className="absolute top-0 h-full w-0.5 bg-terracotta"
          style={{ left: `calc(${(b.typical / barMax) * 100}% - 1px)` }}
        />
      </div>
    </div>
  );
}

export default function SpendingBenchmarksCard({ data }) {
  const hasInsights = data?.insights?.length > 0;
  const hasRefs = Object.keys(data?.benchmarks || {}).length > 0;
  if (!data || (!hasInsights && !hasRefs)) return null;

  return (
    <Card className="card-surface p-6" data-testid="spending-benchmarks-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <GraphUp className="w-5 h-5 text-muted" strokeWidth={1.5} />
          <h2 className="font-serif text-xl text-charcoal">Spending Benchmarks</h2>
        </div>
        <p className="text-xs text-muted flex items-center gap-1.5">
          <InfoCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
          {hasInsights
            ? 'Your allocation vs. typical South Asian wedding'
            : 'Reference ranges for South Asian wedding spend'}
        </p>
      </div>
      <div className="space-y-3 mt-4">
        {hasInsights
          ? data.insights.map((ins) => <InsightRow key={ins.category} ins={ins} />)
          : Object.entries(data.benchmarks).map(([cat, b]) => <ReferenceRow key={cat} cat={cat} b={b} />)}
      </div>
      <p className="text-xs text-muted mt-4 italic">
        {hasInsights
          ? 'Solid bar = your actual % of total spend. Vertical line = typical benchmark.'
          : 'Start logging expenses to compare your allocation against these benchmarks.'}
      </p>
    </Card>
  );
}
